import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';

// Pages
import HomePage from '@/pages/HomePage';
import AboutPage from '@/pages/AboutPage';
import ServicesPage from '@/pages/ServicesPage';
import ProjectsPage from '@/pages/ProjectsPage';
import BlogPage from '@/pages/BlogPage';
import BlogDetailPage from '@/pages/BlogDetailPage';
import ContactPage from '@/pages/ContactPage';
import FAQPage from '@/pages/FAQPage';
import CareerPage from '@/pages/CareerPage';
import CertificatesPage from '@/pages/CertificatesPage';
import QuotePage from '@/pages/QuotePage';
import PaymentPage from '@/pages/PaymentPage';
import PaymentCallbackPage from '@/pages/PaymentCallbackPage';

// Lazy load Chatbot to improve initial page load
import Chatbot from '@/components/Chatbot';

// Admin Pages
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminServices from '@/pages/admin/AdminServices';
import AdminProjects from '@/pages/admin/AdminProjects';
import AdminBlog from '@/pages/admin/AdminBlog';
import AdminMessages from '@/pages/admin/AdminMessages';
import AdminQuotes from '@/pages/admin/AdminQuotes';
import AdminFAQ from '@/pages/admin/AdminFAQ';
import AdminJobs from '@/pages/admin/AdminJobs';
import AdminApplications from '@/pages/admin/AdminApplications';
import AdminCertificates from '@/pages/admin/AdminCertificates';
import AdminPaymentLinks from '@/pages/admin/AdminPaymentLinks';
import AdminTechnicalDocs from '@/pages/admin/AdminTechnicalDocs';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminSiteSettings from '@/pages/admin/AdminSiteSettings';
import AdminMailSettings from '@/pages/admin/AdminMailSettings';
import AdminFileShare from '@/pages/admin/AdminFileShare';
import AdminUsers from '@/pages/admin/AdminUsers';
import ScrollToTop from '@/components/ScrollToTop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Axios retry interceptor for network errors (ERR_CONNECTION_REFUSED, timeout, etc.)
const MAX_RETRIES = 2;
const RETRY_DELAY = 1500;

axios.interceptors.response.use(null, async (error) => {
  const config = error.config;
  if (!config || config._retryCount >= MAX_RETRIES) return Promise.reject(error);

  // Only retry on network errors and 502/503/504
  const isNetworkError = !error.response && error.code !== 'ERR_CANCELED';
  const isServerError = error.response && [502, 503, 504].includes(error.response.status);

  if (isNetworkError || isServerError) {
    config._retryCount = (config._retryCount || 0) + 1;
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * config._retryCount));
    return axios(config);
  }

  return Promise.reject(error);
});

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function App() {
  const [language, setLanguage] = useState('tr');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const storedUser = localStorage.getItem('admin_user');
    
    if (token && storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Fetch fresh user data (permissions may have been updated)
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const freshUser = res.data;
          setUser(freshUser);
          localStorage.setItem('admin_user', JSON.stringify(freshUser));
        })
        .catch(() => { /* ignore - will use cached data */ });
    }
    setLoading(false);
    
    // Setup axios interceptor for token expiry
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if error is 401 (Unauthorized) and message contains "expired"
        if (error.response?.status === 401) {
          const errorMessage = error.response?.data?.detail || '';
          if (errorMessage.toLowerCase().includes('expired') || 
              errorMessage.toLowerCase().includes('token') ||
              errorMessage.toLowerCase().includes('invalid')) {
            
            // Only logout and redirect if not already on login page
            if (window.location.pathname !== '/admin/login') {
              // Token expired or invalid - logout user
              console.log('Token expired, logging out...');
              
              // Save current path to redirect after login
              if (window.location.pathname.startsWith('/admin')) {
                sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
              }
              
              logout();
              
              // Show user-friendly message
              if (window.location.pathname.startsWith('/admin')) {
                window.location.href = '/admin/login?expired=true';
              }
            }
          }
        }
        return Promise.reject(error);
      }
    );
    
    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'tr' ? 'en' : 'tr');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <HelmetProvider>
      <LanguageContext.Provider value={{ language, toggleLanguage }}>
        <AuthContext.Provider value={{ user, login, logout }}>
          <div className="App">
            <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogDetailPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/career" element={<CareerPage />} />
              <Route path="/certificates" element={<CertificatesPage />} />
              <Route path="/quote" element={<QuotePage />} />
              <Route path="/pay/:linkId" element={<PaymentPage />} />
              <Route path="/payment-callback" element={<PaymentCallbackPage />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={!user ? <AdminLogin /> : <Navigate to="/admin/dashboard" />} />
              <Route path="/admin/dashboard" element={user ? <AdminDashboard /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/services" element={user ? <AdminServices /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/projects" element={user ? <AdminProjects /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/blog" element={user ? <AdminBlog /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/messages" element={user ? <AdminMessages /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/quotes" element={user ? <AdminQuotes /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/faq" element={user ? <AdminFAQ /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/jobs" element={user ? <AdminJobs /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/applications" element={user ? <AdminApplications /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/certificates" element={user ? <AdminCertificates /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/payment-links" element={user ? <AdminPaymentLinks /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/technical-docs" element={user ? <AdminTechnicalDocs /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/settings" element={user ? <AdminSettings /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/site-settings" element={user ? <AdminSiteSettings /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/mail-settings" element={user ? <AdminMailSettings /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/file-share" element={user ? <AdminFileShare /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/users" element={user ? <AdminUsers /> : <Navigate to="/admin/login" />} />
            </Routes>
            <Chatbot />
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </AuthContext.Provider>
    </LanguageContext.Provider>
    </HelmetProvider>
  );
}

export default App;
export { API };